import { useCallback, useEffect, useRef, useState } from 'react';
import type { CluePayload } from '@guess/engine';
import { useMusicVolume } from '../audio/music-volume';
import type { ClueProps } from './ClueRenderer';
import s from './clue.module.scss';

type AudioPayload = Extract<CluePayload, { kind: 'audio' }>;

/**
 * L'extrait sonore : on lit `durationMs` a partir de `startMs`, pas une note
 * de plus. La fenetre est une donnee du serveur, et deborder rendrait le
 * palier suivant inutile.
 *
 * Deux contraintes de navigateur commandent tout ce fichier :
 *   - la lecture automatique est bloquee tant que le joueur n'a pas interagi
 *     avec la page. On tente, et on retombe sur un bouton si ca echoue —
 *     jamais un ecran muet sans explication ;
 *   - il n'existe pas d'evenement « fin de fenetre ». On surveille donc la
 *     position et on coupe nous-memes.
 *
 * Le composant n'est PAS remonte entre deux questions (pas de `key` cote
 * `RoundScreen`) : seul `payload.url` change. Un nouveau palier de la MEME
 * question ne doit donc jamais faire repartir la lecture du debut — l'extrait
 * s'est juste allonge, on reprend simplement la ou on en etait (voire on
 * relance si le palier precedent avait deja coupe la lecture en pause). Ne
 * reinitialiser `currentTime` que sur un changement de piste (`payload.url`)
 * evite le "coupe puis redemarre du debut" a chaque palier.
 *
 * Cote serveur, la fenetre de chaque palier couvre tout le palier (voir
 * `buildAudioReveal`) : le son ne s'arrete donc jamais au moment ou la cote
 * change, seulement une fois, a la duree maximale.
 */
export function AudioClue({ payload, stageToken }: ClueProps<AudioPayload>) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const lastUrlRef = useRef<string | null>(null);
  const volume = useMusicVolume();

  // Avant le `play()` du premier effet (ordre de declaration) : le son ne
  // demarre jamais a plein volume avant que le reglage ne s'applique.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const play = useCallback(
    (mode: 'restart' | 'resume' | 'replay') => {
      const audio = audioRef.current;
      if (!audio) return;
      const exhausted = audio.currentTime >= (payload.startMs + payload.durationMs) / 1000;
      // Changement de palier une fois la fenetre epuisee : le son s'est arrete
      // volontairement (duree max), ce n'est pas au palier de le relancer.
      if (mode === 'resume' && exhausted) return;
      // Le joueur reclique sur Ecouter apres la fin : on rejoue l'extrait
      // plutot que de le couper aussitot.
      if (mode === 'restart' || (mode === 'replay' && exhausted)) {
        audio.currentTime = payload.startMs / 1000;
      }
      void audio
        .play()
        .then(() => {
          setPlaying(true);
          setBlocked(false);
        })
        .catch(() => {
          // Lecture refusee : c'est au joueur de la demander.
          setPlaying(false);
          setBlocked(true);
        });
    },
    [payload.startMs, payload.durationMs],
  );

  // Nouvelle piste : on repart du debut. Meme piste, nouveau palier : on
  // reprend / relance sans re-sauter en arriere.
  useEffect(() => {
    const isNewTrack = lastUrlRef.current !== payload.url;
    lastUrlRef.current = payload.url;
    play(isNewTrack ? 'restart' : 'resume');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payload.url lu via la ref, pas une dep d'animation
  }, [play, stageToken]);

  // La coupure en fin de fenetre. `timeupdate` suffit largement : il se
  // declenche plusieurs fois par seconde et l'oreille ne compte pas les
  // millisecondes.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const endSec = (payload.startMs + payload.durationMs) / 1000;

    const onTime = () => {
      if (audio.currentTime >= endSec) {
        audio.pause();
        setPlaying(false);
      }
    };
    audio.addEventListener('timeupdate', onTime);
    return () => audio.removeEventListener('timeupdate', onTime);
  }, [payload.startMs, payload.durationMs]);

  return (
    <div className={s.audioStage}>
      <audio ref={audioRef} src={payload.url} preload="auto" />

      <button type="button" className={s.audioButton} onClick={() => play('replay')} data-playing={playing}>
        <span className={s.audioGlyph} aria-hidden="true">
          {playing ? '||' : '>'}
        </span>
        <span>{playing ? 'Lecture' : 'Ecouter'}</span>
      </button>

      {/* Une forme d'onde decorative : elle bouge pendant la lecture et se
          fige a l'arret. Purement indicative, aucune donnee reelle. */}
      <div className={s.waveform} data-playing={playing} aria-hidden="true">
        {Array.from({ length: 24 }, (_, index) => (
          <span key={index} style={{ animationDelay: `${index * 60}ms` }} />
        ))}
      </div>

      {blocked ? <p className={s.audioHint}>Ton navigateur attend un clic pour jouer le son.</p> : null}
    </div>
  );
}
